/**
 * Upload Service
 *
 * Uploads a user data/document file to S3 using a two-step **presigned PUT**:
 *   1. `POST /uploads` (Cognito-auth) with {filename, contentType} → returns a
 *      short-lived presigned PUT URL + the object key.
 *   2. `PUT` the raw bytes directly to S3 (off the API Gateway payload path).
 * The frontend then passes the key on the next agent turn; the runtime downloads
 * it and routes it by type (bloque `document`, bloque `image`, o sandbox del Code
 * Interpreter).
 *
 * Éste es el carril de TODOS los adjuntos, imágenes incluidas (2026-08-18). Antes
 * las imágenes viajaban en base64 dentro del cuerpo del turno y el WebACL las
 * bloqueaba con un 403 arriba de 8 KB (`SizeRestrictions_BODY`), o sea siempre.
 */

// The uploads route lives on the same REST API as the feedback endpoint, whose
// root (ending in "/") is published as `feedbackApiUrl` in aws-exports.json.
let cachedBaseUrl = ""

async function loadBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  if (!config.feedbackApiUrl) {
    throw new Error("Upload API URL not configured (feedbackApiUrl missing)")
  }
  cachedBaseUrl = config.feedbackApiUrl as string
  return cachedBaseUrl
}

/** A reference to an uploaded file, passed on the next turn's `uploads` array. */
export interface UploadReference {
  s3Key: string
  filename: string
  contentType: string
}

// Extension → MIME fallback. Browsers frequently report an empty or wrong
// `file.type` for text/markdown/csv, which the server allow-list would reject;
// infer from the extension so those uploads succeed.
//
// `webp`/`gif` faltaban acá, y por eso el permiso que se les dio en el presign era
// inalcanzable desde la UI: un .webp entra con `file.type = "image/webp"`, que no
// estaba en `ALLOWED_CONTENT_TYPES`, así que caía al fallback por extensión, que
// tampoco lo tenía, y terminaba pidiendo la firma como
// `application/octet-stream` — que el presign rechaza. Es la primera de las cuatro
// tablas que tienen que coincidir; ver la nota en `VALID_UPLOAD_CONTENT_TYPES`
// (`lambdas/agentcore_chat/handler.js`) para las otras tres y para el modo de falla
// cuando discrepan.
const EXT_TO_CONTENT_TYPE: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
}

// Content types the backend accepts (mirrors the presign Lambda allow-list).
const ALLOWED_CONTENT_TYPES = new Set(Object.values(EXT_TO_CONTENT_TYPE))

/** Resolve a reliable content type: trust file.type only if it's allowed, else
 * infer from the extension. */
function resolveContentType(file: File): string {
  if (file.type && ALLOWED_CONTENT_TYPES.has(file.type)) return file.type
  const ext = file.name.split(".").pop()?.toLowerCase() ?? ""
  return EXT_TO_CONTENT_TYPE[ext] || file.type || "application/octet-stream"
}

/** Shape returned by the presign endpoint. */
interface PresignResponse {
  uploadUrl: string
  key: string
  expiresIn: number
}

/**
 * Upload one file via presigned PUT and return its S3 reference.
 *
 * @param file - the file picked in the composer.
 * @param _sessionId - the active conversation id (reserved; the key is scoped
 *   server-side by the caller's Cognito sub, not the session).
 * @param idToken - Cognito ID token for the API Gateway authorizer.
 * @returns the stored file's S3 key + metadata.
 * @throws if the API URL is not configured, the type is rejected, or S3 PUT fails.
 */
export async function uploadFile(
  file: File,
  _sessionId: string,
  idToken: string
): Promise<UploadReference> {
  const base = await loadBaseUrl()
  const contentType = resolveContentType(file)

  // 1. Ask the API for a presigned PUT URL (validates the content type).
  const presignRes = await fetch(`${base}uploads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ filename: file.name, contentType }),
  })
  if (!presignRes.ok) {
    const detail = await presignRes.json().catch(() => ({}))
    throw new Error(detail.message || detail.error || `HTTP ${presignRes.status}`)
  }
  const { uploadUrl, key } = (await presignRes.json()) as PresignResponse

  // 2. PUT the raw bytes straight to S3. The headers MUST match what the URL was
  // signed for (Content-Type + SSE-KMS), or S3 returns SignatureDoesNotMatch.
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
      "x-amz-server-side-encryption": "aws:kms",
    },
    body: file,
  })
  if (!putRes.ok) {
    throw new Error(`S3 upload failed: HTTP ${putRes.status}`)
  }

  return { s3Key: key, filename: file.name, contentType }
}
